/**
 * DEMO-ONLY: Demo State Manager
 *
 * Manages persistent state for integrated auto-scheduling demo scenarios
 * using localStorage to track created entities for cleanup/reset functionality.
 */

export interface CareRequirement {
  id?: number;
  visitTypeId: number;
  serviceIds: number[];
  duration: string;
  rruleFreq: 'daily' | 'weekly';
  rruleInterval: number;
  rruleDtstart: string;
  rruleUntil: string;
  earliestStartTime?: string;
  latestStartTime?: string;
  minTimeFromLastVisit?: number;
  maxTimeFromLastVisit?: number;
}

export interface MockPatientDetails {
  id: number;
  firstName: string;
  lastName: string;
  mrn: string;
  dateOfBirth: string;
  phone: string;
  addressStreet: string;
  addressCity: string;
  addressState: string;
  addressZip: string;
}

export interface MockVisitDetails {
  id: number;
  startTime: string;
  endTime: string;
  eventStatus: string;
  visitType: {
    id: number;
    name: string;
  };
  providers: Array<{
    id: number;
    firstName: string;
    lastName: string;
  }>;
  services: Array<{
    id: number;
    name: string;
  }>;
}

export interface MockEpisodeDetails {
  id: number;
  admissionDate: string;
  expectedDischargeDate: string;
  currentDay: number;
  expectedLOS: number;
  admissionScheduled?: boolean;
  carePattern: {
    name: string;
    visitsPerDay: number;
    duration: number;
  };
}

export interface Scenario1State {
  patientId: number;
  episodeId: number;
  visitIds: number[];
  executedAt: string;
  patientDetails?: MockPatientDetails;
  episodeDetails?: MockEpisodeDetails;
  visits?: MockVisitDetails[];
}

export interface Scenario2State {
  patientId: number;
  episodeId: number;
  originalCareRequirements: CareRequirement[];
  canceledVisitIds: number[];
  newVisitIds: number[];
  executedAt: string;
  patientDetails?: MockPatientDetails;
  episodeDetails?: MockEpisodeDetails;
  visits?: MockVisitDetails[];
}

export interface Scenario3State {
  patientId: number;
  episodeId: number;
  originalCareRequirements: CareRequirement[];
  updatedVisitIds: number[];
  executedAt: string;
  patientDetails?: MockPatientDetails;
  episodeDetails?: MockEpisodeDetails;
  visits?: MockVisitDetails[];
}

export interface DemoState {
  scenario1?: Scenario1State;
  scenario2?: Scenario2State;
  scenario3?: Scenario3State;
  admissionCoordination?: AdmissionCoordinationState;
}

export interface AdmissionCoordinationState {
  needsCoordination: boolean;
  patientId: number;
  episodeId: number;
  patientName: string;
  mrn: string;
  admissionOrderTime: string;
  diagnosis: string;
  expectedLOS: number;
  supplies: {
    vendor: 'Velocare' | 'Cardinal' | 'MedicallyHome' | null;
    ordered: boolean;
    estimatedDelivery: string | null;
    items: string[];
  };
  transportation: {
    ordered: boolean;
    estimatedArrival: string | null;
    duration: number;
  };
  recommendedTime: string | null;
  completed: boolean;
  admissionVisitId?: number;
  todaysVisitIds?: number[];
}

const STORAGE_KEY = 'demo-auto-scheduling-state';

/**
 * Load demo state from localStorage
 */
export function getDemoState(): DemoState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Failed to load demo state from localStorage:', error);
    return {};
  }
}

/**
 * Save demo state after scenario execution
 */
export function saveDemoState(scenarioKey: 'scenario1', data: Scenario1State): void;
export function saveDemoState(scenarioKey: 'scenario2', data: Scenario2State): void;
export function saveDemoState(scenarioKey: 'scenario3', data: Scenario3State): void;
export function saveDemoState(
  scenarioKey: 'scenario1' | 'scenario2' | 'scenario3',
  data: Scenario1State | Scenario2State | Scenario3State
): void {
  try {
    const state = getDemoState();
    (state[scenarioKey] as any) = {
      ...data,
      executedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    console.log(`Demo state saved for ${scenarioKey}:`, data);
  } catch (error) {
    console.error(`Failed to save demo state for ${scenarioKey}:`, error);
    throw new Error('Could not persist demo state. Please try again.');
  }
}

/**
 * Clear specific scenario state
 */
export function clearScenarioState(
  scenarioKey: 'scenario1' | 'scenario2' | 'scenario3'
): void {
  try {
    const state = getDemoState();
    delete state[scenarioKey];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    console.log(`Demo state cleared for ${scenarioKey}`);
  } catch (error) {
    console.error(`Failed to clear demo state for ${scenarioKey}:`, error);
  }
}

/**
 * Clear all demo state except admission coordination
 * (admission coordination should persist until user completes or cancels it)
 * NOTE: Run counter is preserved to allow patient iteration across sessions
 */
export function clearAllDemoState(): void {
  try {
    // Save admission coordination state and run counter before clearing
    const currentState = getDemoState();
    const admissionCoord = currentState.admissionCoordination;
    const runCounter = localStorage.getItem(DEMO_RUN_COUNTER_KEY);

    // Clear everything
    localStorage.removeItem(STORAGE_KEY);

    // Restore admission coordination if it exists and is not completed
    if (admissionCoord && admissionCoord.needsCoordination && !admissionCoord.completed) {
      const newState: DemoState = {
        admissionCoordination: admissionCoord,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
      console.log('All demo state cleared (run counter preserved), preserved pending admission coordination');
    } else {
      console.log('All demo state cleared (run counter preserved)');
    }

    // Note: Run counter is intentionally NOT cleared to maintain patient iteration
    if (runCounter) {
      console.log(`Run counter preserved at: ${runCounter}`);
    }
  } catch (error) {
    console.error('Failed to clear all demo state:', error);
  }
}

/**
 * Clear all demo state INCLUDING the run counter
 * (only used when user explicitly wants to reset everything)
 */
export function clearAllDemoStateAndCounter(): void {
  try {
    // Save admission coordination state before clearing
    const currentState = getDemoState();
    const admissionCoord = currentState.admissionCoordination;

    // Clear everything including counter
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(DEMO_RUN_COUNTER_KEY);

    // Restore admission coordination if it exists and is not completed
    if (admissionCoord && admissionCoord.needsCoordination && !admissionCoord.completed) {
      const newState: DemoState = {
        admissionCoordination: admissionCoord,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
      console.log('All demo state cleared (including run counter), but preserved pending admission coordination');
    } else {
      console.log('All demo state cleared (including run counter)');
    }
  } catch (error) {
    console.error('Failed to clear all demo state:', error);
  }
}

/**
 * Check if any scenario has been executed
 */
export function hasAnyScenarioExecuted(): boolean {
  const state = getDemoState();
  return !!(state.scenario1 || state.scenario2 || state.scenario3);
}

/**
 * Get execution timestamp for a scenario
 */
export function getScenarioExecutedAt(
  scenarioKey: 'scenario1' | 'scenario2' | 'scenario3'
): string | null {
  const state = getDemoState();
  return state[scenarioKey]?.executedAt || null;
}

/**
 * Get all unique patient IDs from all scenarios
 */
export function getAllDemoPatientIds(): number[] {
  const state = getDemoState();
  const patientIds = new Set<number>();

  if (state.scenario1?.patientId) patientIds.add(state.scenario1.patientId);
  if (state.scenario2?.patientId) patientIds.add(state.scenario2.patientId);
  if (state.scenario3?.patientId) patientIds.add(state.scenario3.patientId);

  return Array.from(patientIds);
}

const DEMO_RUN_COUNTER_KEY = 'demo-auto-scheduling-run-counter';
const MOCK_PATIENTS_COUNT = 20;

/**
 * Get and increment the demo run counter
 */
function getAndIncrementRunCounter(): number {
  try {
    const stored = localStorage.getItem(DEMO_RUN_COUNTER_KEY);
    const currentCount = stored ? parseInt(stored, 10) : 0;
    const nextCount = currentCount + 1;
    localStorage.setItem(DEMO_RUN_COUNTER_KEY, nextCount.toString());

    // Verify it was actually saved
    const verified = localStorage.getItem(DEMO_RUN_COUNTER_KEY);
    console.log(`[Demo Counter] Incremented from ${currentCount} to ${nextCount}, verified in storage: ${verified}`);

    return currentCount;
  } catch (error) {
    console.error('Failed to manage run counter:', error);
    return 0;
  }
}

/**
 * Generate human-readable timestamp in format YYYY-MM-DD-HH-mm-ss
 */
function generateTimestamp(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}-${hours}-${minutes}-${seconds}`;
}

/**
 * Convert iteration number to ordinal string (1st, 2nd, 3rd, etc.)
 */
function toOrdinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export interface DemoPatientWithMetadata {
  firstName: string;
  lastName: string;
  mrn: string;
  dateOfBirth: string;
  phone: string;
  addressStreet: string;
  addressLineTwo?: string;
  addressCity: string;
  addressState: string;
  addressZip: string;
  patientNotes: string;
}

/**
 * Get the next demo patient with unique MRN and iteration-based naming
 *
 * This function:
 * - In live mode: Cycles through 20 mock patients, increments counter, adds iteration suffix
 * - In simulation mode: Always returns first patient without incrementing counter
 * - Appends iteration number to last name (e.g., Doe-AS, Doe-AS2, Doe-AS3)
 * - Generates unique MRN with human-readable timestamp
 * - Creates dynamic patient notes based on iteration
 */
export function getNextDemoPatient(mockPatients: any[], isLiveMode: boolean = false): DemoPatientWithMetadata {
  let runCount: number;
  let patientIndex: number;
  let iteration: number;

  console.log(`[getNextDemoPatient] Called with isLiveMode=${isLiveMode}, mockPatients.length=${mockPatients.length}`);

  if (isLiveMode) {
    // In live mode, increment the counter and cycle through patients
    runCount = getAndIncrementRunCounter();
    patientIndex = runCount % MOCK_PATIENTS_COUNT;
    iteration = Math.floor(runCount / MOCK_PATIENTS_COUNT) + 1;
    console.log(`[getNextDemoPatient] Live mode: runCount=${runCount}, patientIndex=${patientIndex}, iteration=${iteration}`);
  } else {
    // In simulation mode, always use first patient without incrementing
    runCount = 0;
    patientIndex = 0;
    iteration = 1;
    console.log(`[getNextDemoPatient] Simulation mode: always using first patient`);
  }

  const basePatient = mockPatients[patientIndex];
  const timestamp = generateTimestamp();

  // Append iteration number to last name (only if > 1)
  const lastName = iteration === 1
    ? basePatient.lastName
    : `${basePatient.lastName}${iteration}`;

  // Generate unique MRN with human-readable timestamp
  const mrn = `${basePatient.mrn}-${timestamp}`;

  // Generate dynamic patient notes based on iteration
  const patientNotes = isLiveMode
    ? `${toOrdinal(iteration)} iteration of auto-scheduling demo scenario`
    : 'Demo scenario (simulation mode)';

  const result = {
    firstName: basePatient.firstName,
    lastName,
    mrn,
    dateOfBirth: basePatient.dateOfBirth,
    phone: basePatient.phone,
    addressStreet: basePatient.addressStreet,
    addressLineTwo: basePatient.addressLineTwo,
    addressCity: basePatient.addressCity,
    addressState: basePatient.addressState,
    addressZip: basePatient.addressZip,
    patientNotes,
  };

  console.log(`[getNextDemoPatient] Returning patient: ${result.firstName} ${result.lastName} (MRN: ${result.mrn})`);

  return result;
}

/**
 * Save admission coordination state
 */
export function saveAdmissionCoordination(data: AdmissionCoordinationState): void {
  try {
    const state = getDemoState();
    state.admissionCoordination = data;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    console.log('Admission coordination state saved:', data);
  } catch (error) {
    console.error('Failed to save admission coordination state:', error);
    throw new Error('Could not persist admission coordination state.');
  }
}

/**
 * Get admission coordination state
 */
export function getAdmissionCoordination(): AdmissionCoordinationState | null {
  const state = getDemoState();
  return state.admissionCoordination || null;
}

/**
 * Clear admission coordination state
 */
export function clearAdmissionCoordination(): void {
  try {
    const state = getDemoState();
    delete state.admissionCoordination;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    console.log('Admission coordination state cleared');
  } catch (error) {
    console.error('Failed to clear admission coordination state:', error);
  }
}

/**
 * Check if admission coordination is needed
 */
export function needsAdmissionCoordination(): boolean {
  const coordination = getAdmissionCoordination();
  return coordination ? coordination.needsCoordination && !coordination.completed : false;
}

/**
 * Get episode details (admission/discharge dates) from demo state by episode ID
 */
export function getEpisodeDetailsFromDemoState(episodeId: number): MockEpisodeDetails | null {
  const state = getDemoState();

  // Check all scenarios for this episode ID
  if (state.scenario1?.episodeId === episodeId && state.scenario1?.episodeDetails) {
    return state.scenario1.episodeDetails;
  }
  if (state.scenario2?.episodeId === episodeId && state.scenario2?.episodeDetails) {
    return state.scenario2.episodeDetails;
  }
  if (state.scenario3?.episodeId === episodeId && state.scenario3?.episodeDetails) {
    return state.scenario3.episodeDetails;
  }

  return null;
}

/**
 * Mark an episode's admission visit as scheduled so the patient status
 * transitions from "New Admission" to "On Track".
 */
export function markAdmissionScheduled(episodeId: number): void {
  const state = getDemoState();
  if (state.scenario1?.episodeId === episodeId && state.scenario1.episodeDetails) {
    saveDemoState('scenario1', {
      ...state.scenario1,
      episodeDetails: { ...state.scenario1.episodeDetails, admissionScheduled: true },
    });
  } else if (state.scenario2?.episodeId === episodeId && state.scenario2.episodeDetails) {
    saveDemoState('scenario2', {
      ...state.scenario2,
      episodeDetails: { ...state.scenario2.episodeDetails, admissionScheduled: true },
    });
  } else if (state.scenario3?.episodeId === episodeId && state.scenario3.episodeDetails) {
    saveDemoState('scenario3', {
      ...state.scenario3,
      episodeDetails: { ...state.scenario3.episodeDetails, admissionScheduled: true },
    });
  }
}
